/* iter213 — Super Admin · AI Usage & Billing Control Center.
   Provider status · Usage dashboard · Tenant rollup · Audit log ·
   Budget settings · Model registry.  */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Sparkles, Activity, AlertTriangle, ArrowLeft, RefreshCcw,
  Wallet, BarChart3, Database, Save, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const usd = (n) =>
  typeof n === 'number'
    ? n < 0.01 ? `$${n.toFixed(6)}` : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';
const num = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : '—');
const pct = (n) => (typeof n === 'number' ? `${n.toFixed(1)}%` : '—');

const ALERT_STYLES = {
  ok:        'bg-emerald-50  text-emerald-800 border-emerald-200',
  warn:      'bg-amber-50    text-amber-900   border-amber-300',
  high:      'bg-orange-50   text-orange-900  border-orange-300',
  critical:  'bg-rose-50     text-rose-900    border-rose-300',
  not_configured: 'bg-slate-100 text-slate-700 border-slate-300',
};

export default function SuperAiUsage() {
  const [token]      = useState(() => localStorage.getItem('token') || '');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [usage,    setUsage]    = useState(null);
  const [tenants,  setTenants]  = useState([]);
  const [audit,    setAudit]    = useState([]);
  const [settings, setSettings] = useState(null);
  const [models,   setModels]   = useState([]);
  const [period,   setPeriod]   = useState('monthly');
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetDraft, setBudgetDraft]   = useState('');
  const [defaultModelDraft, setDefaultModelDraft] = useState('');
  const [savedFlash, setSavedFlash] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [p, u, t, a, s] = await Promise.all([
        axios.get(`${API}/api/super/ai/provider-status`, { headers }),
        axios.get(`${API}/api/super/ai/usage?period=${period}`, { headers }),
        axios.get(`${API}/api/super/ai/tenants?period=${period}`, { headers }),
        axios.get(`${API}/api/super/ai/audit?limit=50`, { headers }),
        axios.get(`${API}/api/super/ai/settings`, { headers }),
      ]);
      setProvider(p.data?.data);
      setUsage(u.data?.data);
      setTenants(t.data?.data?.rows || []);
      setAudit(a.data?.data?.rows || []);
      setSettings(s.data?.data?.settings);
      setModels(s.data?.data?.models || []);
      setBudgetDraft(String((s.data?.data?.settings?.monthly_budget_usd) || ''));
      setDefaultModelDraft(s.data?.data?.settings?.default_model || '');
    } catch (e) {
      console.error('[ai-usage] load failed', e);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (token) load(); }, [token, period]);

  async function saveBudget() {
    setSavingBudget(true);
    try {
      await axios.post(`${API}/api/super/ai/settings`, {
        monthly_budget_usd: parseFloat(budgetDraft) || 0,
      }, { headers });
      setSavedFlash('budget');
      setTimeout(() => setSavedFlash(null), 2000);
      await load();
    } finally { setSavingBudget(false); }
  }

  async function switchDefaultModel(model) {
    try {
      await axios.post(`${API}/api/super/ai/default-model`, { model }, { headers });
      setSavedFlash('model');
      setTimeout(() => setSavedFlash(null), 2000);
      await load();
    } catch (e) { console.error(e); }
  }

  const alertLevel = provider?.alert_level || 'not_configured';
  const balancePct = provider?.monthly_budget_usd > 0
    ? (provider.balance_usd / provider.monthly_budget_usd) * 100 : null;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-10" data-testid="super-ai-usage">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-violet-700 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Super Admin · iter213
            </div>
            <h1 className="text-3xl font-semibold text-slate-900 mt-1">AI Usage &amp; Billing Control Center</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Every LLM call across RestroPulse routes through the central AI Gateway. This page surfaces real-time provider status,
              cumulative spend, tenant-level consumption, audit log, and budget alerts.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/super/dashboard"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-100"
                  data-testid="ai-usage-back">
              <ArrowLeft className="w-4 h-4" /> Super Admin
            </Link>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="ai-usage-refresh">
              <RefreshCcw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Alert banner */}
        {provider && alertLevel !== 'ok' && (
          <div className={`border rounded-xl px-4 py-3 flex items-start gap-3 ${ALERT_STYLES[alertLevel]}`}
               data-testid="ai-usage-alert-banner">
            <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
            <div className="text-sm flex-1">
              <div className="font-semibold uppercase tracking-wide text-[11px]">
                {alertLevel === 'not_configured' ? 'Budget not configured' : `${alertLevel} balance`}
              </div>
              <div className="mt-0.5">
                {alertLevel === 'not_configured'
                  ? 'Set a monthly budget below so the platform can track remaining credits and warn before exhaustion.'
                  : `Only ${pct(balancePct)} of the configured monthly budget remains. Consider topping up via the Emergent Universal Key page (Profile → Universal Key → Add Balance) before reaching zero.`}
              </div>
              {provider.proxy_signal?.proxy_max_budget_usd != null && (
                <div className="mt-1 text-[11px] text-slate-700">
                  <strong>Proxy signal:</strong> the LiteLLM proxy last reported a per-key cap of
                  {' '}<strong>${provider.proxy_signal.proxy_max_budget_usd}</strong> with observed cumulative cost
                  {' '}<strong>${provider.proxy_signal.proxy_observed_cost_usd}</strong>.
                  This is independent of the budget you configure below.
                </div>
              )}
            </div>
          </div>
        )}

        {/* 1 · Provider Status — 5 KPI cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ai-usage-provider-cards">
          <KpiCard label="Provider · Model" value={provider ? `${provider.provider} · ${provider.model}` : '—'}
                   icon={<Sparkles className="w-4 h-4" />} testid="kpi-provider"/>
          <KpiCard label="Monthly Budget"   value={usd(provider?.monthly_budget_usd)}
                   icon={<Wallet className="w-4 h-4" />} testid="kpi-budget"/>
          <KpiCard label="Current Spend"    value={usd(provider?.current_spend_usd)}
                   sub={`${num(provider?.calls_this_period)} calls`}
                   icon={<Activity className="w-4 h-4" />} testid="kpi-spend"/>
          <KpiCard label="Remaining Balance" value={usd(provider?.balance_usd)}
                   sub={typeof balancePct === 'number' ? `${pct(balancePct)} left` : '—'}
                   icon={<Wallet className="w-4 h-4" />}
                   tone={alertLevel === 'critical' ? 'rose' : alertLevel === 'high' ? 'orange' : alertLevel === 'warn' ? 'amber' : 'emerald'}
                   testid="kpi-balance"/>
          <KpiCard label="Avg Cost / Report" value={usd(provider?.avg_cost_per_report_usd)}
                   sub={provider?.estimated_reports_remaining != null
                        ? `~${num(provider.estimated_reports_remaining)} reports left`
                        : '— need spend data'}
                   icon={<BarChart3 className="w-4 h-4" />} testid="kpi-avgcost"/>
        </div>

        {/* 2 · Usage Dashboard (period toggle + trend mini-chart) */}
        <Card data-testid="ai-usage-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Usage Dashboard</h2>
                <p className="text-xs text-slate-500">Aggregated from the per-call audit log into daily buckets.</p>
              </div>
              <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="ai-usage-period-toggle">
                {['daily','weekly','monthly','trend30'].map(p => (
                  <button key={p}
                          onClick={() => setPeriod(p)}
                          className={`px-3 py-1.5 text-xs ${period === p ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                          data-testid={`ai-usage-period-${p}`}>
                    {p === 'trend30' ? 'Trend 30d' : p[0].toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-3 mt-4">
              <KpiInline label="Calls"             value={num(usage?.totals?.calls)} />
              <KpiInline label="Prompt tokens"     value={num(usage?.totals?.prompt_tokens)} />
              <KpiInline label="Completion tokens" value={num(usage?.totals?.completion_tokens)} />
              <KpiInline label="Cost (USD)"        value={usd(usage?.totals?.cost_usd)} />
            </div>
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                Cost trend · last 30 UTC days
              </div>
              <TrendChart rows={usage?.trend_30d || []} />
            </div>
          </CardContent>
        </Card>

        {/* 3 · Tenant Rollup */}
        <Card data-testid="ai-usage-tenants-card">
          <CardContent className="p-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Tenant-Level Consumption</h2>
            <p className="text-xs text-slate-500 mb-3">
              Architecture ready for SaaS plans — quotas are tracked but not enforced. Last activity = most recent rollup date.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ai-usage-tenants-table">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-3">Tenant</th>
                    <th className="text-right">AI Calls</th>
                    <th className="text-right">Prompt Tok</th>
                    <th className="text-right">Completion Tok</th>
                    <th className="text-right">Cost (USD)</th>
                    <th className="text-right pl-3">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.length === 0 && (
                    <tr><td colSpan={6} className="py-5 text-center text-slate-400">No tenant usage in this period yet.</td></tr>
                  )}
                  {tenants.map(t => (
                    <tr key={t.tenant_id} className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`ai-usage-tenant-row-${t.tenant_id}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">
                        {t.tenant_name || t.tenant_id}
                        <div className="text-[10px] text-slate-400">{t.tenant_id}</div>
                      </td>
                      <td className="text-right">{num(t.calls)}</td>
                      <td className="text-right text-slate-600">{num(t.prompt_tokens)}</td>
                      <td className="text-right text-slate-600">{num(t.completion_tokens)}</td>
                      <td className="text-right font-medium text-slate-800">{usd(t.cost_usd)}</td>
                      <td className="text-right pl-3 text-slate-500 text-xs">{t.last_active || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* 4 · Audit Log */}
        <Card data-testid="ai-usage-audit-card">
          <CardContent className="p-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Audit Log <span className="text-[11px] text-slate-400 font-normal">(latest 50)</span></h2>
            <p className="text-xs text-slate-500 mb-3">Timestamp · user · feature · model · tokens · cost · latency · status.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="ai-usage-audit-table">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-2 pr-3">Timestamp UTC</th>
                    <th className="text-left">User</th>
                    <th className="text-left">Feature</th>
                    <th className="text-left">Model</th>
                    <th className="text-right">Tokens (in / out)</th>
                    <th className="text-right">Cost</th>
                    <th className="text-right">ms</th>
                    <th className="text-center pl-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.length === 0 && (
                    <tr><td colSpan={8} className="py-5 text-center text-slate-400">No AI calls audited yet.</td></tr>
                  )}
                  {audit.map(r => (
                    <tr key={r.audit_id} className="border-b border-slate-100"
                        data-testid={`ai-audit-row-${r.audit_id}`}>
                      <td className="py-1.5 pr-3 text-slate-500">{r.created_at?.replace('T',' ').slice(0,19)}</td>
                      <td className="text-slate-700">{r.user_email || r.user_id || '—'}</td>
                      <td><Badge variant="secondary">{r.feature}</Badge></td>
                      <td className="text-slate-600">{r.model}</td>
                      <td className="text-right text-slate-600">{num(r.prompt_tokens)} / {num(r.completion_tokens)}</td>
                      <td className="text-right font-medium">{usd(r.cost_usd)}</td>
                      <td className="text-right text-slate-500">{num(r.latency_ms)}</td>
                      <td className="text-center pl-3">
                        {r.status === 'ok'
                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3"/>ok</span>
                          : <span className="inline-flex items-center gap-1 text-rose-700" title={r.error || ''}>
                              <AlertTriangle className="w-3 h-3"/>err</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* 5 · Settings — Budget + Default Model */}
        <Card data-testid="ai-usage-settings-card">
          <CardContent className="p-5">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Budget &amp; Model Registry</h2>
            <p className="text-xs text-slate-500 mb-4">
              Change the default model without redeploying. Edit alert thresholds and the monthly budget envelope.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Budget */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Monthly budget (USD)</div>
                <div className="flex gap-2">
                  <Input type="number" value={budgetDraft} step="1" min="0"
                         onChange={e => setBudgetDraft(e.target.value)}
                         data-testid="ai-usage-budget-input" className="max-w-xs" />
                  <Button onClick={saveBudget} disabled={savingBudget} data-testid="ai-usage-budget-save">
                    <Save className="w-3.5 h-3.5 mr-1" /> {savingBudget ? 'Saving…' : 'Save'}
                  </Button>
                  {savedFlash === 'budget' && <span className="text-xs text-emerald-700 self-center">Saved ✓</span>}
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  Alerts fire at {settings?.alert_warn_pct ?? 25}% (warn), {settings?.alert_high_pct ?? 10}% (high), {settings?.alert_critical_pct ?? 5}% (critical) remaining.
                </div>
              </div>
              {/* Default model */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Default model</div>
                <div className="flex flex-wrap gap-2" data-testid="ai-usage-models-pillrow">
                  {models.map(m => {
                    const isDefault = m.is_default && m.status === 'active';
                    return (
                      <button key={m.model}
                              onClick={() => switchDefaultModel(m.model)}
                              disabled={m.status !== 'active'}
                              data-testid={`ai-usage-model-${m.model}`}
                              className={`text-left p-2 rounded-lg border text-xs transition ${
                                isDefault
                                  ? 'border-violet-500 bg-violet-50 text-violet-900'
                                  : 'border-slate-200 bg-white hover:border-slate-400 text-slate-700'}`}>
                        <div className="font-semibold flex items-center gap-1">
                          {isDefault && <CheckCircle2 className="w-3 h-3 text-violet-700" />}
                          {m.model}
                        </div>
                        <div className="text-[10px] text-slate-500">{m.provider} · in ${m.input_per_m_usd}/M · out ${m.output_per_m_usd}/M</div>
                      </button>
                    );
                  })}
                </div>
                {savedFlash === 'model' && <div className="text-xs text-emerald-700 mt-2">Default model switched ✓</div>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Architecture note */}
        <Card className="border-dashed border-violet-200 bg-violet-50/40">
          <CardContent className="p-4 text-xs text-violet-900">
            <div className="font-semibold uppercase tracking-wider text-[10px] mb-1">Architecture · single AI entry point</div>
            <div className="leading-relaxed">
              All current and future AI features (Executive Recommendation, Review Intelligence, Menu Engineering, BSC Insights, Sales Narrative, Financial Commentary…) call
              <code className="mx-1 px-1.5 py-0.5 rounded bg-violet-100">services.ai.ai_gateway.complete_json()</code>.
              Direct imports of <code>emergentintegrations</code>, <code>litellm</code>, OpenAI/Claude/Gemini SDKs are forbidden outside <code>services/ai/</code>.
              Every call is audited (<code>db.ai_audit_log</code>) and rolled up into <code>db.ai_usage_daily</code>.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ── small UI primitives ────────────────────────────────────────────── */
function KpiCard({ label, value, sub, icon, tone = 'slate', testid }) {
  const toneCls = {
    slate:   'border-slate-200 bg-white',
    emerald: 'border-emerald-200 bg-emerald-50/40',
    amber:   'border-amber-200 bg-amber-50/40',
    orange:  'border-orange-200 bg-orange-50/40',
    rose:    'border-rose-200 bg-rose-50/40',
  }[tone];
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{icon}{label}</div>
      <div className="text-base font-semibold text-slate-900 mt-1 break-all">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function KpiInline({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/50 p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function TrendChart({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-slate-400 italic">No usage data yet.</div>;
  }
  const maxCost = Math.max(...rows.map(r => r.cost_usd || 0)) || 1;
  return (
    <div className="flex items-end gap-0.5 h-24" data-testid="ai-usage-trend-chart">
      {rows.map(r => {
        const h = Math.max(2, Math.round((r.cost_usd || 0) / maxCost * 100));
        return (
          <div key={r.date_utc} title={`${r.date_utc} · ${r.calls} calls · ${usd(r.cost_usd)}`}
               className="flex-1 bg-violet-300 hover:bg-violet-500 transition rounded-t-sm"
               style={{ height: `${h}%` }} />
        );
      })}
    </div>
  );
}

export const SuperAiUsage_Component = SuperAiUsage;
